/**
 * Client-side SPA Router for vanilla TypeScript
 * Handles route registration, navigation, and view rendering
 */

/**
 * Route parameters extracted from the URL
 */
export type RouteParams = Record<string, string>;

/**
 * Query string parameters
 */
export type QueryParams = Record<string, string>;

/**
 * Route context passed to view handlers
 */
export interface RouteContext {
  params: RouteParams;
  query: QueryParams;
  path: string;
}

/**
 * Function type for rendering views
 */
export type ViewRenderer = (context: RouteContext) => HTMLElement | string | Promise<HTMLElement | string>;

/**
 * Route guard function - returns true to allow navigation, false to block
 * Can also return a redirect path string
 */
export type RouteGuard = (context: RouteContext) => boolean | string | Promise<boolean | string>;

/**
 * Route configuration
 */
export interface Route {
  path: string;
  view: ViewRenderer;
  guards?: RouteGuard[];
}

/**
 * Internal route with compiled regex pattern
 */
interface CompiledRoute {
  path: string;
  view: ViewRenderer;
  guards?: RouteGuard[];
  pattern: RegExp;
  paramNames: string[];
}

/**
 * Router configuration options
 */
export interface RouterOptions {
  /**
   * The root element to render views into
   */
  rootElement: HTMLElement;
  /**
   * Optional base path for all routes (e.g., '/app')
   */
  basePath?: string;
  /**
   * View to render when no route matches (404)
   */
  notFoundView?: ViewRenderer;
  /**
   * Global guards applied to all routes
   */
  globalGuards?: RouteGuard[];
}

/**
 * Event emitted on route change
 */
export interface RouteChangeEvent {
  from: string | null;
  to: string;
  context: RouteContext;
}

/**
 * Router event listener
 */
export type RouteChangeListener = (event: RouteChangeEvent) => void;

/**
 * Client-side SPA Router
 */
export class Router {
  private routes: CompiledRoute[] = [];
  private rootElement: HTMLElement;
  private basePath: string;
  private notFoundView: ViewRenderer;
  private globalGuards: RouteGuard[];
  private currentPath: string | null = null;
  private listeners: RouteChangeListener[] = [];
  private isInitialized = false;

  constructor(options: RouterOptions) {
    this.rootElement = options.rootElement;
    this.basePath = options.basePath?.replace(/\/$/, '') || '';
    this.globalGuards = options.globalGuards || [];
    this.notFoundView = options.notFoundView || this.defaultNotFoundView;
  }

  /**
   * Register a route
   */
  register(path: string, view: ViewRenderer, guards?: RouteGuard[]): this {
    const { pattern, paramNames } = this.compilePath(path);
    const route: CompiledRoute = {
      path,
      view,
      pattern,
      paramNames,
    };
    if (guards) {
      route.guards = guards;
    }
    this.routes.push(route);
    return this;
  }

  /**
   * Register multiple routes at once
   */
  registerRoutes(routes: Route[]): this {
    for (const route of routes) {
      this.register(route.path, route.view, route.guards);
    }
    return this;
  }

  /**
   * Initialize the router and start listening for navigation events
   */
  init(): this {
    if (this.isInitialized) {
      return this;
    }

    // Handle browser back/forward buttons
    window.addEventListener('popstate', this.handlePopState.bind(this));

    // Handle initial route
    this.navigate(this.getCurrentPath(), true);

    this.isInitialized = true;
    return this;
  }

  /**
   * Navigate to a path programmatically
   */
  async navigate(path: string, replace = false): Promise<void> {
    const fullPath = this.normalizePath(path);
    const context = this.createContext(fullPath);

    // Find matching route
    const match = this.findRoute(fullPath);

    if (match) {
      context.params = match.params;

      // Run guards
      const guardResult = await this.runGuards(match.route, context);

      if (guardResult !== true) {
        // Guard returned false or redirect path
        if (typeof guardResult === 'string') {
          // Redirect to the path returned by guard
          await this.navigate(guardResult, replace);
          return;
        }
        // Navigation blocked
        return;
      }

      // Update browser history
      this.updateHistory(fullPath, replace);

      // Emit route change event
      const previousPath = this.currentPath;
      this.currentPath = fullPath;
      this.emitRouteChange(previousPath, fullPath, context);

      // Render the view
      await this.renderView(match.route.view, context);
    } else {
      // No matching route - show 404
      this.updateHistory(fullPath, replace);
      const previousPath = this.currentPath;
      this.currentPath = fullPath;
      this.emitRouteChange(previousPath, fullPath, context);
      await this.renderView(this.notFoundView, context);
    }
  }

  /**
   * Navigate back in history
   */
  back(): void {
    window.history.back();
  }

  /**
   * Navigate forward in history
   */
  forward(): void {
    window.history.forward();
  }

  /**
   * Add a route change listener
   */
  onRouteChange(listener: RouteChangeListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Get the current path including query string
   */
  getCurrentPath(): string {
    let path = window.location.pathname;
    if (this.basePath && path.startsWith(this.basePath)) {
      path = path.slice(this.basePath.length) || '/';
    }
    // Include query string if present
    if (window.location.search) {
      path += window.location.search;
    }
    return path;
  }

  /**
   * Get the current query parameters
   */
  getCurrentQuery(): QueryParams {
    return this.parseQueryString(window.location.search);
  }

  /**
   * Destroy the router and clean up event listeners
   */
  destroy(): void {
    window.removeEventListener('popstate', this.handlePopState.bind(this));
    this.listeners = [];
    this.routes = [];
    this.isInitialized = false;
  }

  /**
   * Compile a path pattern into a regex
   */
  private compilePath(path: string): { pattern: RegExp; paramNames: string[] } {
    const paramNames: string[] = [];

    // Escape special regex characters except for our parameter syntax
    let regexPath = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Replace :param patterns with named capture groups
    regexPath = regexPath.replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, (_, name) => {
      paramNames.push(name);
      return '([^/]+)';
    });

    // Handle optional trailing slash
    regexPath = regexPath.replace(/\/$/, '');

    // Create regex that matches the path exactly (with optional trailing slash)
    const pattern = new RegExp(`^${regexPath}/?$`);

    return { pattern, paramNames };
  }

  /**
   * Find a matching route for a path
   */
  private findRoute(path: string): { route: CompiledRoute; params: RouteParams } | null {
    // Remove query string for matching
    const pathWithoutQuery = path.split('?')[0] ?? path;

    for (const route of this.routes) {
      const match = pathWithoutQuery.match(route.pattern);
      if (match) {
        const params: RouteParams = {};
        route.paramNames.forEach((name, index) => {
          const value = match[index + 1];
          if (value !== undefined) {
            params[name] = decodeURIComponent(value);
          }
        });
        return { route, params };
      }
    }
    return null;
  }

  /**
   * Create a route context
   */
  private createContext(path: string): RouteContext {
    const parts = path.split('?');
    const pathPart = parts[0] ?? '/';
    const queryPart = parts[1];
    return {
      params: {},
      query: this.parseQueryString(queryPart ? `?${queryPart}` : ''),
      path: pathPart,
    };
  }

  /**
   * Parse query string into object
   */
  private parseQueryString(search: string): QueryParams {
    const params: QueryParams = {};
    const searchParams = new URLSearchParams(search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  /**
   * Run route guards
   */
  private async runGuards(route: CompiledRoute, context: RouteContext): Promise<boolean | string> {
    // Run global guards first
    for (const guard of this.globalGuards) {
      const result = await guard(context);
      if (result !== true) {
        return result;
      }
    }

    // Run route-specific guards
    if (route.guards) {
      for (const guard of route.guards) {
        const result = await guard(context);
        if (result !== true) {
          return result;
        }
      }
    }

    return true;
  }

  /**
   * Update browser history
   */
  private updateHistory(path: string, replace: boolean): void {
    const fullPath = this.basePath + path;
    // Only append location.search if path doesn't already contain query params
    const hasQueryInPath = path.includes('?');
    const url = hasQueryInPath
      ? fullPath + window.location.hash
      : fullPath + window.location.search + window.location.hash;

    if (replace) {
      window.history.replaceState({ path: fullPath }, '', url);
    } else {
      window.history.pushState({ path: fullPath }, '', url);
    }
  }

  /**
   * Render a view into the root element
   */
  private async renderView(view: ViewRenderer, context: RouteContext): Promise<void> {
    const content = await view(context);

    if (typeof content === 'string') {
      this.rootElement.innerHTML = content;
    } else {
      this.rootElement.innerHTML = '';
      this.rootElement.appendChild(content);
    }
  }

  /**
   * Handle browser back/forward navigation
   */
  private handlePopState(_event: PopStateEvent): void {
    const path = this.getCurrentPath();
    this.navigate(path, true);
  }

  /**
   * Emit route change event to listeners
   */
  private emitRouteChange(from: string | null, to: string, context: RouteContext): void {
    const event: RouteChangeEvent = { from, to, context };
    for (const listener of this.listeners) {
      listener(event);
    }
  }

  /**
   * Normalize a path (ensure leading slash, handle base path)
   */
  private normalizePath(path: string): string {
    // Handle relative paths
    if (!path.startsWith('/')) {
      path = '/' + path;
    }
    // Remove base path if present
    if (this.basePath && path.startsWith(this.basePath)) {
      path = path.slice(this.basePath.length) || '/';
    }
    return path;
  }

  /**
   * Default 404 view
   */
  private defaultNotFoundView(context: RouteContext): string {
    return `
      <div style="display: flex; justify-content: center; align-items: center; min-height: 100vh; font-family: system-ui, -apple-system, sans-serif;">
        <div style="text-align: center;">
          <h1 style="font-size: 4rem; font-weight: 700; margin-bottom: 1rem; color: #ef4444;">404</h1>
          <p style="font-size: 1.25rem; color: #666; margin-bottom: 2rem;">Page not found</p>
          <p style="color: #999;">The path "${context.path}" does not exist.</p>
        </div>
      </div>
    `;
  }
}

/**
 * Create a router instance
 */
export function createRouter(options: RouterOptions): Router {
  return new Router(options);
}
